/**
 * Thin hook shims over TanStack Router, giving `components/*` and `lib/*`
 * files a `useRouter`/`usePathname`/`useSegments`/`useLocalSearchParams`
 * surface. (Route files under `app/` use the native TanStack hooks directly.)
 */
import {
  useNavigate,
  useRouterState,
  useParams,
  useSearch,
} from "@tanstack/react-router";

/**
 * Call sites pass either a string path or an object form
 * (`{ pathname, params }`). TanStack takes `{ to, search }`, so normalize both
 * into that shape in one place.
 */
type To = string | { pathname: string; params?: Record<string, unknown> };

/**
 * Strip pathless-group segments (`/(app)`, `/(auth)`) from a `to`
 * path. TanStack route groups don't appear in the URL, so `/(app)/admin` must
 * become `/admin`. Many component-level nav calls pass group-prefixed paths,
 * so normalize them here in one place.
 */
function stripGroups(pathname: string): string {
  const cleaned = pathname.replace(/\/\([^)]+\)/g, "");
  return cleaned === "" ? "/" : cleaned;
}

/**
 * Turn a `To` (string or `{ pathname, params }`) into TanStack's
 * `{ to, search }`. Non-dynamic `params` become query/search params, which is
 * how the routes consume them (`validateSearch` + `Route.useSearch()`).
 */
function toNavOptions(to: To): { to: string; search?: Record<string, unknown> } {
  if (typeof to === "string") return { to: stripGroups(to) };
  return {
    to: stripGroups(to.pathname),
    ...(to.params ? { search: to.params } : {}),
  };
}

export function useRouter() {
  const navigate = useNavigate();
  return {
    push: (to: To) => navigate(toNavOptions(to) as never),
    replace: (to: To) => navigate({ ...toNavOptions(to), replace: true } as never),
    navigate: (to: To) => navigate(toNavOptions(to) as never),
    back: () => window.history.back(),
    canGoBack: () => window.history.length > 1,
    setParams: (params: Record<string, unknown>) =>
      navigate({
        to: "." as never,
        search: ((prev: Record<string, unknown>) => ({
          ...prev,
          ...params,
        })) as never,
        replace: true,
      }),
  };
}

export function usePathname(): string {
  return useRouterState({ select: (s) => s.location.pathname });
}

export function useSegments(): string[] {
  const pathname = usePathname();
  return pathname.split("/").filter(Boolean);
}

export function useLocalSearchParams<
  T extends Record<string, unknown> = Record<string, string>,
>(): T {
  const params = useParams({ strict: false });
  const search = useSearch({ strict: false });
  return { ...(params as object), ...(search as object) } as T;
}
